import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Dialog, Label, Combobox } from "@/components/ui";
import { Form, PlayersServices, FanServices, FormServices, FormRequest } from "@/services/bedrock";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { usePaginatedCombobox } from "@/utils";
import { Plus, WandSparkles, Sparkles, Globe, User, UserCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { FanCreationForm } from "@/components/Forms/FanCreationForm";
import { FormPersonalizationHelpDialog } from "./FormPersonalizationHelpDialog";
import { FormRequestSuccessView } from "./FormRequestSuccessView";
import { FormRequestSettingsView } from "./FormRequestSettingsView";
import { toast } from "sonner";

interface FormRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: Form | null;
  formRequest?: FormRequest | null;
}

const POLLING_MESSAGES = [
  "Tailoring your custom feedback experience...",
  "Generating personalized URL and shortcode...",
  "Coaching our AI on player and fan context...",
  "Translating prompts and finalizing layout...",
  "Synchronizing variables with your SocialCoach dashboard...",
  "Almost ready! Finalizing the feedback request link...",
];

export function FormRequestDialog({ open, onOpenChange, form, formRequest }: FormRequestDialogProps) {
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [linkType, setLinkType] = useState<"general" | "player" | "client">("general");
  const [isCreateFanOpen, setIsCreateFanOpen] = useState(false);
  const [isCreatingFan, setIsCreatingFan] = useState(false);
  const [generatedRequest, setGeneratedRequest] = useState<FormRequest | null>(null);
  const [loadingMsg, setLoadingMsg] = useState(POLLING_MESSAGES[0]);

  // Step 4 settings states
  const [isTest, setIsTest] = useState(false);
  const [useAiPersonalization, setUseAiPersonalization] = useState(false);
  const [contextInstructions, setContextInstructions] = useState("");
  const [internalIdentifier, setInternalIdentifier] = useState("");
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  // Step 2: get players list
  const getPlayers = async (search: string | undefined, page: number) => {
    const result = await PlayersServices.getPlayers({ search, page, perPage: 25 });
    return {
      ...result,
      content: result.content.map(player => ({
        id: player.id,
        label: `${player.fullName || "No Name"} - ${player.email}`,
      })),
    };
  };

  const initialPlayerValue = useMemo(() => {
    if (formRequest?.player) {
      return {
        id: formRequest.player.id,
        label: `${formRequest.player.fullName || "No Name"} - ${formRequest.player.email}`,
      };
    }
    return null;
  }, [formRequest]);

  const players = usePaginatedCombobox(getPlayers, false, initialPlayerValue);

  // Step 3: get fans list (filtered by player if selected)
  const getFans = useCallback(
    async (search: string | undefined, page: number) => {
      const result = await FanServices.getFans({
        search,
        page,
        active: true,
        perPage: 25,
        playerId: players.value?.id,
      } as any);
      return {
        ...result,
        content: result.content.map(fan => ({
          id: fan.id,
          label: `${fan.displayName || "No Name"}${fan.email ? ` - ${fan.email}` : ""}`,
          original: fan,
        })),
      };
    },
    [players.value]
  );

  const initialFanValue = useMemo(() => {
    if (formRequest?.fan) {
      return {
        id: formRequest.fan.id,
        label: `${formRequest.fan.displayName || "No Name"}${
          formRequest.fan.email ? ` - ${formRequest.fan.email}` : ""
        }`,
        original: formRequest.fan,
      };
    }
    return null;
  }, [formRequest]);

  const fans = usePaginatedCombobox(getFans, false, initialFanValue);

  const [prevPlayerId, setPrevPlayerId] = useState<number | undefined | null>(formRequest?.player?.id);

  // Reset dialog state when opening
  useEffect(() => {
    if (open) {
      setCurrentStep(1);
      setIsCreateFanOpen(false);
      setIsCreatingFan(false);
      setGeneratedRequest(null);
      setLoadingMsg(POLLING_MESSAGES[0]);

      if (formRequest) {
        setIsTest(formRequest.isTest || false);
        setUseAiPersonalization(formRequest.useAiPersonalization || false);
        setContextInstructions(formRequest.contextInstructions || "");
        setInternalIdentifier(formRequest.internalIdentifier || "");
        setPrevPlayerId(formRequest.player?.id);

        if (formRequest.fan) {
          setLinkType("client");
        } else if (formRequest.player) {
          setLinkType("player");
        } else {
          setLinkType("general");
        }
      } else {
        setIsTest(false);
        setUseAiPersonalization(false);
        setContextInstructions("");
        setInternalIdentifier("");
        setPrevPlayerId(null);
        setLinkType("general");
        players.onValueChange(null as any, undefined);
        fans.onValueChange(null as any, undefined);
      }
    }
  }, [open, formRequest]);

  // Clear fan selection when player selection changes manually
  useEffect(() => {
    const currentPlayerId = players.value?.id;
    if (currentPlayerId !== prevPlayerId) {
      fans.onValueChange(null as any, undefined);
      fans.reset(); // Flush and reload the fans list with the new player selection context
      setPrevPlayerId(currentPlayerId);
    }
  }, [players.value, prevPlayerId]);

  // Loading messages rotation during polling
  useEffect(() => {
    let msgInterval: any = null;
    if (currentStep === 5) {
      msgInterval = setInterval(() => {
        setLoadingMsg(prev => {
          const remaining = POLLING_MESSAGES.filter(m => m !== prev);
          return remaining[Math.floor(Math.random() * remaining.length)];
        });
      }, 3500);
    }
    return () => {
      if (msgInterval) clearInterval(msgInterval);
    };
  }, [currentStep]);

  // Polling loop for request status
  useEffect(() => {
    let pollInterval: any = null;

    if (currentStep === 5 && generatedRequest?.id) {
      const poll = async () => {
        try {
          const result = await FormServices.getFormRequests({
            formRequestId: generatedRequest.id,
          } as any);
          const request = result.content?.find(r => r.id === generatedRequest.id);

          if (request) {
            if (request.status === "ready") {
              setGeneratedRequest(request);
              setCurrentStep(6); // Success step!
            }
          }
        } catch (err) {
          console.error("Error polling form request", err);
        }
      };

      poll();
      pollInterval = setInterval(poll, 3000);
    }

    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [currentStep, generatedRequest?.id]);

  const handleFanCreated = async (formData: any) => {
    setIsCreatingFan(true);
    try {
      const newFan = await FanServices.createFan({
        ...formData,
        playerId: players.value?.id,
        active: true,
      });
      fans.onValueChange(
        {
          id: newFan.id,
          label: `${newFan.displayName || "No Name"}${newFan.email ? ` - ${newFan.email}` : ""}`,
        } as any,
        undefined
      );
      setIsCreateFanOpen(false);
      setCurrentStep(4); // Automatically advance to settings step
    } catch (err) {
      console.error("Failed to create fan", err);
    } finally {
      setIsCreatingFan(false);
    }
  };

  const createRequestMutation = useMutation({
    mutationFn: (params: any) => {
      if (!form?.id) throw new Error("No form ID found");
      return FormServices.createFormRequest(form.id, params);
    },
    onSuccess: (data: FormRequest) => {
      queryClient.invalidateQueries({ queryKey: ["formRequests"] });
      setGeneratedRequest(data);
      setCurrentStep(5); // Switch to polling step!
    },
    onError: err => {
      console.error("Failed to create form request", err);
      toast.error("Error generating form link. Please try again.");
    },
  });

  const updateRequestMutation = useMutation({
    mutationFn: ({ formRequestId, params }: { formRequestId: number; params: any }) => {
      if (!form?.id) throw new Error("No form ID found");
      return FormServices.updateFormRequest(form.id, formRequestId, params);
    },
    onSuccess: (data: FormRequest) => {
      queryClient.invalidateQueries({ queryKey: ["formRequests"] });
      queryClient.invalidateQueries({ queryKey: ["formRequestsList"] });
      setGeneratedRequest(data);
      setCurrentStep(5); // Switch to polling step!
    },
    onError: err => {
      console.error("Failed to update form request", err);
      toast.error("Error saving form link. Please try again.");
    },
  });

  const handleCreateRequest = () => {
    const params: any = {
      isTest,
      useAiPersonalization: form?.allowAiPersonalization ? useAiPersonalization : false, // Guard AI personalization if not allowed on form level
      internalIdentifier: internalIdentifier || undefined,
    };

    if (linkType === "player" || linkType === "client") {
      if (players.value?.id) {
        params.playerId = players.value.id;
      }
    }
    if (linkType === "client") {
      if (fans.value?.id) {
        params.fanId = fans.value.id;
      }
    }
    if (form?.allowAiPersonalization && useAiPersonalization && contextInstructions) {
      params.contextInstructions = contextInstructions;
    }

    if (formRequest?.id) {
      updateRequestMutation.mutate({ formRequestId: formRequest.id, params });
    } else {
      createRequestMutation.mutate(params);
    }
  };

  const hasSelectedPlayer = !!players.value;
  const hasSelectedFan = !!fans.value;
  const isEdit = !!formRequest;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="lg">
      <Dialog.Header>
        <Dialog.Title>
          {currentStep === 5 && "Personalizing Link"}
          {currentStep === 6 && "Form Link Ready"}
          {currentStep <= 4 && (isEdit ? "Edit Form Link Settings" : "Generate Form Link")}
        </Dialog.Title>
        <Dialog.Description>
          {currentStep === 5 && "Please wait while our AI system customizes the request flow..."}
          {currentStep === 6 && "Share this unique URL with your contacts to gather feedback."}
          {currentStep <= 4 &&
            (isEdit
              ? "Modify settings and custom instructions for this specific link."
              : `Create a personalized, unique feedback link for ${form?.displayName || "this form"}.`)}
        </Dialog.Description>
      </Dialog.Header>

      <Dialog.Content className="max-h-[65vh] overflow-y-auto">
        {/* Step Stepper Header (Hidden during polling/success) */}
        {currentStep <= 4 && (
          <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-6 select-none">
            {(() => {
              const stepsList = [
                { label: "Request Type", num: 1 },
                ...(linkType !== "general" ? [{ label: "Player", num: 2 }] : []),
                ...(linkType === "client" ? [{ label: "Client Contact", num: 3 }] : []),
                { label: "Settings", num: 4 },
              ];

              return stepsList.map((step, idx, arr) => {
                let stepStatus: "active" | "complete" | "pending" = "pending";
                if (currentStep === step.num) {
                  stepStatus = "active";
                } else if (currentStep > step.num) {
                  stepStatus = "complete";
                } else if (linkType === "general" && currentStep === 4 && step.num === 4) {
                  stepStatus = "active";
                }

                return (
                  <React.Fragment key={step.num}>
                    <div className="flex items-center gap-2">
                      <div
                        className={cn(
                          "size-6 rounded-full flex items-center justify-center text-xs font-bold font-mono border transition-all duration-200",
                          stepStatus === "active"
                            ? "bg-primary border-primary text-white ring-4 ring-primary/10"
                            : stepStatus === "complete"
                            ? "bg-emerald-500 border-emerald-500 text-white"
                            : "bg-slate-50 border-slate-200 text-slate-400"
                        )}
                      >
                        {stepStatus === "complete" ? "✓" : idx + 1}
                      </div>
                      <span
                        className={cn(
                          "text-xs font-bold font-sans",
                          stepStatus === "active"
                            ? "text-slate-800"
                            : stepStatus === "complete"
                            ? "text-emerald-600"
                            : "text-slate-400"
                        )}
                      >
                        {step.label}
                      </span>
                    </div>
                    {idx < arr.length - 1 && (
                      <div
                        className={cn(
                          "h-0.5 flex-1 mx-4 transition-all duration-200",
                          stepStatus === "complete" ? "bg-emerald-200" : "bg-slate-100"
                        )}
                      />
                    )}
                  </React.Fragment>
                );
              });
            })()}
          </div>
        )}

        {/* STEP 1: CHOOSE LINK TYPE */}
        {currentStep === 1 && (
          <div className="flex flex-col gap-4 py-2 animate-in fade-in duration-200">
            <div className="flex flex-col gap-1 mb-4">
              <h3 className="text-base font-bold text-slate-800 font-sans leading-none">Select Link Audience</h3>
              <p className="text-xs text-slate-500 leading-normal font-sans">
                Choose who this link is for to determine the level of branding and personalization.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                {
                  id: "general",
                  title: "General Link",
                  description:
                    "A reusable, public feedback link. Ideal for sharing on your website, email signature, or social media pages.",
                  icon: Globe,
                  color: "border-blue-200 bg-blue-50/10 text-blue-600 hover:border-blue-300 hover:bg-blue-50/20",
                  selectedColor: "border-blue-500 bg-blue-500/5 text-blue-600 ring-4 ring-blue-500/10",
                  iconBg: "bg-blue-600 text-white",
                },
                {
                  id: "player",
                  title: "For a Player",
                  description:
                    "A link mapped to a specific Loan Officer or Player. Submitted reviews automatically attach to their profile.",
                  icon: User,
                  color:
                    "border-indigo-200 bg-indigo-50/10 text-indigo-600 hover:border-indigo-300 hover:bg-indigo-50/20",
                  selectedColor: "border-indigo-500 bg-indigo-500/5 text-indigo-600 ring-4 ring-indigo-500/10",
                  iconBg: "bg-indigo-600 text-white",
                },
                {
                  id: "client",
                  title: "For a Client",
                  description:
                    "A one-time targeted request for a specific client (Fan), enabling personalization and rich tracking.",
                  icon: UserCheck,
                  color:
                    "border-purple-200 bg-purple-50/10 text-purple-600 hover:border-purple-300 hover:bg-purple-50/20",
                  selectedColor: "border-purple-500 bg-purple-500/5 text-purple-600 ring-4 ring-purple-500/10",
                  iconBg: "bg-purple-600 text-white",
                },
              ].map(option => {
                const isSelected = linkType === option.id;
                const Icon = option.icon;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => {
                      setLinkType(option.id as any);
                    }}
                    className={cn(
                      "flex flex-col items-center text-center p-5 rounded-2xl border-2 transition-all duration-200 outline-none w-full cursor-pointer h-full",
                      isSelected ? option.selectedColor : option.color
                    )}
                  >
                    <div
                      className={cn(
                        "size-12 rounded-xl flex items-center justify-center mb-4 transition-all duration-200",
                        isSelected ? option.iconBg : "bg-white border border-current/20 text-current"
                      )}
                    >
                      <Icon className="size-6" />
                    </div>
                    <span className="font-bold text-sm leading-none mb-2 font-sans select-none text-slate-800">
                      {option.title}
                    </span>
                    <p className="text-xs text-muted-foreground leading-normal font-sans select-none">
                      {option.description}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 2: CHOOSE PLAYER */}
        {currentStep === 2 && (
          <div className="flex flex-col gap-4 py-2 animate-in fade-in duration-200">
            <div className="flex flex-col gap-2.5">
              <Label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Select A Player</Label>
              <Combobox {...players} placeholder="Search for a Player..." className="w-full" />
            </div>
          </div>
        )}

        {/* STEP 3: CHOOSE FAN */}
        {currentStep === 3 && (
          <div className="flex flex-col gap-5 animate-in fade-in duration-200 select-none">
            {/* Label & Dropdown Selection */}
            <div className="flex flex-col gap-2">
              <Label className="text-xs font-bold text-slate-500 uppercase tracking-wide block">Select a Fan</Label>
              <Combobox
                {...fans}
                placeholder={
                  players.value
                    ? `Search ${players.value.label.split(" - ")[0]}'s contacts...`
                    : "Search all contacts..."
                }
                className="w-full"
              />
              <p className="text-[10px] text-slate-400 leading-normal mt-1 font-sans">
                {players.value
                  ? "Showing only contacts assigned to the selected Player."
                  : "Search through all available contacts in the workspace."}
              </p>
            </div>

            {/* Visual 'OR' Divider */}
            <div className="relative flex py-2 items-center">
              <div className="flex-grow border-t border-slate-200/50" />
              <span className="flex-shrink mx-4 text-xs font-bold text-slate-300 uppercase tracking-widest font-mono">
                OR
              </span>
              <div className="flex-grow border-t border-slate-200/50" />
            </div>

            {/* Prominent Create New Fan Button */}
            <div className="flex flex-col items-center justify-center pt-2">
              <Button
                variant="outline"
                size="lg"
                type="button"
                onClick={() => setIsCreateFanOpen(true)}
                className="w-full sm:w-auto px-8 h-11 rounded-xl border-dashed border-2 hover:border-solid border-slate-200 hover:border-primary/50 text-slate-600 hover:text-primary hover:bg-primary/5 font-sans font-bold transition duration-200 shadow-3xs"
              >
                <Plus className="mr-2 size-4 text-current" />
                Create a New Fan
              </Button>
            </div>
          </div>
        )}

        {/* STEP 4: SETTINGS */}
        {currentStep === 4 && (
          <FormRequestSettingsView
            isTest={isTest}
            onIsTestChange={setIsTest}
            useAiPersonalization={useAiPersonalization}
            onUseAiPersonalizationChange={setUseAiPersonalization}
            contextInstructions={contextInstructions}
            onContextInstructionsChange={setContextInstructions}
            internalIdentifier={internalIdentifier}
            onInternalIdentifierChange={setInternalIdentifier}
            onHelpClick={() => setHelpDialogOpen(true)}
            hideAiPersonalization={!form?.allowAiPersonalization}
            playerLabel={players.value?.label ? players.value.label.split(" - ")[0] : undefined}
            fanLabel={fans.value?.label ? fans.value.label.split(" - ")[0] : undefined}
          />
        )}

        {/* STEP 5: POLLING LOADING STATE */}
        {currentStep === 5 && (
          <div className="flex flex-col items-center justify-center py-8 px-4 text-center animate-in fade-in duration-500 select-none">
            <div className="relative mb-8">
              <div className="absolute inset-0 scale-150 bg-primary/10 rounded-full blur-3xl animate-pulse" />
              <div className="relative z-10 flex items-center justify-center size-20 bg-white rounded-2xl shadow-xl border border-primary/10">
                <WandSparkles className="size-10 text-primary animate-pulse" />
              </div>
            </div>

            <div className="flex flex-col items-center gap-2 mb-6">
              <h3 className="text-xl font-bold text-slate-800 tracking-tight">Personalizing Form Link...</h3>
              <p className="text-muted-foreground text-xs max-w-xs mx-auto font-sans italic transition-all duration-300 min-h-[32px] flex items-center justify-center">
                "{loadingMsg}"
              </p>
            </div>

            <div className="w-40 bg-muted rounded-full h-1 overflow-hidden">
              <div className="bg-primary h-1 rounded-full animate-pulse w-full" />
            </div>
            <span className="text-xs text-muted-foreground italic mt-6 leading-normal block max-w-[250px]">
              Personalizing each link dynamically can take up to a minute. Please don't close this window.
            </span>

            {/* AI Explanation Teaser Card */}
            <div className="mt-8 p-4 bg-slate-50 border border-slate-200/60 rounded-2xl max-w-sm mx-auto flex flex-col items-center gap-2">
              <span className="text-xs font-semibold text-slate-700">Curious how it works under the hood?</span>
              <p className="text-[11px] text-slate-500 leading-normal">
                Learn how our separate translation and personalization engines customize the experience for each client.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setHelpDialogOpen(true)}
                className="mt-1 h-9 rounded-xl px-4 text-xs font-semibold flex items-center gap-1.5 shadow-sm bg-white"
              >
                <Sparkles className="size-3.5 text-primary animate-pulse" />
                <span>See How AI Works</span>
              </Button>
            </div>
          </div>
        )}

        {/* STEP 6: SUCCESS & COPY STATE */}
        {currentStep === 6 && generatedRequest && (
          <FormRequestSuccessView
            url={generatedRequest.url}
            summaryItems={[
              {
                label: "Link Type",
                value:
                  linkType === "general" ? "General Link" : linkType === "player" ? "For a Player" : "For a Client",
              },
              {
                label: "Assigned Player",
                value: players.value?.label ? players.value.label.split(" - ")[0] : "General Link (Unassigned)",
              },
              {
                label: "Fan Contact",
                value: fans.value?.label ? fans.value.label : "General Link (Unassigned)",
              },
              {
                label: "AI Personalized",
                value: (
                  <span className={cn("font-semibold", useAiPersonalization ? "text-primary" : "text-slate-800")}>
                    {useAiPersonalization ? "Enabled" : "Disabled"}
                  </span>
                ),
              },
              ...(internalIdentifier
                ? [
                    {
                      label: "Internal ID",
                      value: <span className="font-mono text-slate-700">{internalIdentifier}</span>,
                    },
                  ]
                : []),
            ]}
          />
        )}
      </Dialog.Content>

      <Dialog.Footer>
        <div className="flex justify-between w-full items-center select-none">
          <div>
            {currentStep > 1 && currentStep <= 4 && (
              <Button
                variant="ghost"
                onClick={() => {
                  if (currentStep === 4) {
                    if (linkType === "general") setCurrentStep(1);
                    else if (linkType === "player") setCurrentStep(2);
                    else if (linkType === "client") setCurrentStep(3);
                  } else if (currentStep === 3) {
                    setCurrentStep(2);
                  } else if (currentStep === 2) {
                    setCurrentStep(1);
                  }
                }}
                type="button"
              >
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {currentStep <= 4 && (
              <Button variant="outline" onClick={() => onOpenChange(false)} type="button">
                Cancel
              </Button>
            )}

            {currentStep === 1 && (
              <Button
                onClick={() => {
                  if (linkType === "general") {
                    players.onValueChange(null as any, undefined);
                    fans.onValueChange(null as any, undefined);
                    setCurrentStep(4);
                  } else {
                    setCurrentStep(2);
                  }
                }}
                type="button"
              >
                Next
              </Button>
            )}

            {currentStep === 2 && (
              <Button
                onClick={() => {
                  if (linkType === "player") {
                    fans.onValueChange(null as any, undefined);
                    setCurrentStep(4);
                  } else if (linkType === "client") {
                    setCurrentStep(3);
                  }
                }}
                type="button"
              >
                {hasSelectedPlayer ? "Next" : "Skip / Next"}
              </Button>
            )}

            {currentStep === 3 && (
              <Button
                onClick={() => {
                  setCurrentStep(4);
                }}
                disabled={!hasSelectedFan}
                type="button"
              >
                Next
              </Button>
            )}

            {currentStep === 4 && (
              <Button
                onClick={handleCreateRequest}
                loading={createRequestMutation.isPending || updateRequestMutation.isPending}
                type="button"
              >
                {isEdit ? "Save Link" : "Create Link"}
              </Button>
            )}

            {currentStep === 6 && (
              <Button onClick={() => onOpenChange(false)} type="button">
                Done
              </Button>
            )}
          </div>
        </div>
      </Dialog.Footer>

      {/* Form Personalization Help Dialog */}
      <FormPersonalizationHelpDialog open={helpDialogOpen} onOpenChange={setHelpDialogOpen} />

      {/* Nested Create New Fan Dialog */}
      <Dialog open={isCreateFanOpen} onOpenChange={setIsCreateFanOpen} size="lg">
        <Dialog.Header>
          <Dialog.Title className="flex items-center gap-2 text-base font-bold font-sans select-none">
            <Plus className="size-5 text-primary" />
            <span>Create New Contact</span>
          </Dialog.Title>
          <Dialog.Description className="select-none text-xs">
            Add a new client (Fan) to your dashboard. This contact will be automatically assigned to the selected Player
            if specified.
          </Dialog.Description>
        </Dialog.Header>

        <Dialog.Content className="max-h-[60vh] overflow-y-auto py-4 px-6">
          <FanCreationForm
            onSubmit={handleFanCreated}
            isSubmitting={isCreatingFan}
            hidePlayerSelect={hasSelectedPlayer}
            defaultValues={{
              displayName: "",
              email: "",
              phoneNumber: "",
              fanType: "customer",
              notes: "",
              playerId: players.value?.id || null,
            }}
          />
        </Dialog.Content>

        <Dialog.Footer className="select-none">
          <Button variant="outline" onClick={() => setIsCreateFanOpen(false)} type="button">
            Cancel
          </Button>
        </Dialog.Footer>
      </Dialog>
    </Dialog>
  );
}
